import React, { useEffect, useRef } from "react";
import { Box, Typography } from "@material-ui/core";
import * as MaterialUI from "@material-ui/core";
import * as MaterialIcons from "@material-ui/icons";

import { PluginRuntime, i18nTranslator } from "@markedesk/plugin-sdk/ui";

import api from "../../services/api";
import { mountPluginJsx } from "../PluginBridge/pluginJsx";

/**
 * Campo desenhado pelo PRÓPRIO plugin, em JSX.
 *
 * Existe para o core parar de crescer a cada necessidade de UI. Antes, "quero
 * uma tela de escolha com busca e filtro" virava um tipo de campo novo no
 * frontend — e só o core podia inventar palavra nova, o que amarra o plugin a
 * um release do sistema.
 *
 * O JSX é transpilado pelo Babel (sob demanda) e renderizado com o React DO
 * APP: hooks, context e tema funcionam, e o plugin não carrega bundle próprio.
 * `props.mui` entrega o Material-UI do app, então a tela sai com a cara do
 * sistema em vez de `div` cru.
 *
 * O plugin já roda código no app (o `frontendScript` é avaliado com
 * `new Function`), então isto não amplia a superfície de confiança — é a mesma
 * de sempre: plugin instalado é código confiável.
 */
export default function JsxField({ field, value, setValue, ctx }) {
  // `name` e opcional: um `jsx` SEM name nao tem valor proprio — ele desenha a
  // tela (ou a aba) inteira e grava pelo `setCampo`.
  const noRef = useRef(null);
  const montadoRef = useRef(null);
  const cacheRef = useRef({});
  const [erro, setErro] = React.useState(null);

  // As props precisam ser recalculadas a cada render (o valor muda), mas a
  // montagem acontece uma vez — é o que preserva o estado interno do componente
  // do plugin (um campo de busca não pode perder o texto a cada tecla).
  const props = {
    field,
    value,
    setValue,
    values: ctx?.values,
    /**
     * Grava QUALQUER setting, não só o do próprio campo.
     *
     * É o que permite um `jsx` sem `name` desenhar a tela inteira (ou uma aba
     * inteira) em vez de um campo só. Sem isto, config toda em JSX exigiria um
     * campo declarativo por valor, escondido atrás do JSX.
     */
    setCampo: (nome, valor) => ctx?.onChange({ ...(ctx?.values || {}), [nome]: valor }),
    /** Substitui o conjunto inteiro — para tela que edita vários de uma vez. */
    setValues: (proximos) => ctx?.onChange(proximos),
    routePath: ctx?.routePath,
    pluginId: ctx?.pluginId,
    React,
    mui: MaterialUI,
    icons: MaterialIcons,
    // Idioma da interface. O dicionario e do plugin — o core so diz qual
    // lingua a pessoa esta usando.
    idioma: PluginRuntime.idioma || "pt",
    // Tradutor amarrado a este plugin: `t("token")` dentro da tela.
    t: i18nTranslator(ctx?.pluginId, PluginRuntime.idioma),
    /**
     * Chamadas ao próprio plugin, já autenticadas e com o caminho na frente.
     *
     * Sem isto, o plugin teria que remontar a URL do proxy à mão e reencontrar
     * o token — e erraria, porque o caminho tem o hash da empresa.
     */
    api: {
      get: (caminho, config) => api.get(`${ctx?.routePath || ""}${caminho}`, config),
      post: (caminho, corpo, config) => api.post(`${ctx?.routePath || ""}${caminho}`, corpo, config),
    },
  };

  useEffect(() => {
    let vivo = true;
    if (!field.content || !noRef.current) return undefined;

    mountPluginJsx(noRef.current, field.content, props, cacheRef.current)
      .then((montado) => {
        if (!vivo) {
          montado.unmount();
          return;
        }
        montadoRef.current = montado;
      })
      // Erro de sintaxe no JSX do plugin não pode derrubar a tela de settings
      // inteira: o resto dos campos continua editável, e o problema aparece
      // onde o campo estaria.
      .catch((e) => vivo && setErro(e?.message || String(e)));

    return () => {
      vivo = false;
      if (montadoRef.current) {
        montadoRef.current.unmount();
        montadoRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.content]);

  // Re-render com props novas SEM remontar — preserva o estado do plugin.
  useEffect(() => {
    if (montadoRef.current) montadoRef.current.render(props);
  });

  if (erro) {
    return (
      <Box my={1}>
        <Typography variant="caption" color="error">
          Falha ao renderizar o campo do plugin: {erro}
        </Typography>
      </Box>
    );
  }

  return <Box my={1} ref={noRef} />;
}
